import { create } from 'zustand';

import { CUSTOMER_INITIAL_SORT_STATUS } from '@/configs/filter';
import service from '@/services/index';
import { INITIAL_FILTER_SET_QUERY } from '@/types/dataTableConst';
import { CONTACT_COLUMNS } from '@/types/filterKeyConst';
import filterUtils from '@/utils/filter';
import { deleteObjectField } from '@/utils/object';
import { getCursorInfoV2, getPaginationQueries } from '@/utils/pagination';
import { arrayToMap, buildQuery, pushEntryIntoQueryArr } from '@/utils/util';

import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from './pure/DetailModalStore';

type States = {
  customerMap: CustomerMapT;
  cursors: PaginationCursorsTV2;
  selectedCustomer: string | null;
  selectedCustomerCompanyModal: string | null;
  createModalStatus: boolean;
  page: number;
  listLoading: boolean;
};

export const CUSTOMER_PAGE_SIZE = 20;

type Actions = {
  setState: <T extends keyof States>(state: Record<T, States[T]>) => void;
  setCustomerMap: (customerMap: CustomerMapT) => void;
  setNewCustomer: (customer: CustomerT) => void;
  updateCustomer: (customer: CustomerT) => void;
  addNewCustomer: (customer: PostCustomerParams) => Promise<CustomerT>;
  removeCustomer: (alias: string) => void;
  setSelectedCustomer: (alias: string | null) => void;
  setSelectedCustomerCompanyModal: (alias: string | null) => void;
  setCreateModalStatus: (status: boolean) => void;
  fetchCustomerList: (options?: {
    prevData?: PaginatedData<any>[];
    page?: number;
    searchValue?: string;
    queries?: QueryT[];
    sortStatus?: NotNullableSortStatusT;
    fiterSetQuery?: FilterSetQuery;
  }) => Promise<PaginatedData<CustomerT>[]>;
  fetchCustomer: (alias: string) => Promise<CustomerT>;
  changeCustomerAttr: <K extends keyof UpdatableCustomerAttrs>(
    alias: string,
    target: K,
    value: UpdatableCustomerAttrs[K],
  ) => void;
  setCustomerAttr: <K extends keyof UpdatableCustomerAttrs>(
    alias: string,
    target: K,
    value: UpdatableCustomerAttrs[K],
  ) => void;
};

const initialState: States = {
  customerMap: {},
  cursors: {
    first: null,
    last: null,
  },
  selectedCustomer: null,
  selectedCustomerCompanyModal: null,
  createModalStatus: false,
  page: 0,
  // searchValue: '',
  listLoading: false,
};
const useCustomerStore = create<States & Actions>((set, get) => ({
  // States
  ...initialState,
  // Actions
  setState: (state) => set(state),
  setCustomerMap: (customerMap) => {
    set((state) => ({
      ...state,
      customerMap,
    }));
  },
  addNewCustomer: async (customer) => {
    const data = await service.contact.postCustomer(customer);
    return data;
  },
  setNewCustomer: (customer) => {
    const { page } = get();
    if (page !== 0) return;

    set((state) => {
      let prevArr = Object.values(state.customerMap);
      if (prevArr.length >= CUSTOMER_PAGE_SIZE) {
        prevArr = prevArr.slice(0, -1);
      }
      const newArr = [customer, ...prevArr];
      const newMap = newArr.reduce(
        (acc, value) => {
          acc[value.alias] = value;
          return acc;
        },
        {} as Record<string, CustomerT>,
      );
      return {
        customerMap: newMap,
      };
    });
  },
  updateCustomer: (customer) => {
    const customerMap = { ...get().customerMap };
    const origCustomer = customerMap[customer.alias];
    if (!origCustomer) return;

    Object.assign(origCustomer, customer);
    set((state) => ({
      ...state,
      customerMap: customerMap,
    }));
  },
  removeCustomer: async (alias) => {
    await service.contact.deleteCustomer(alias);
    const newCustomerMap = deleteObjectField(get().customerMap, alias);
    set({ customerMap: newCustomerMap });
  },
  setSelectedCustomer: (key) => {
    const { setNewModal, initModal } = useDetailModalStore.getState();
    set({ selectedCustomer: key });

    if (key) {
      setNewModal({
        alias: key,
        type: DETAIL_MODAL_TYPE.CUSTOMER,
        idx: 0,
      });
    } else {
      initModal();
    }
  },
  setSelectedCustomerCompanyModal: (alias) => {
    set(() => ({
      selectedCustomerCompanyModal: alias,
    }));
  },
  setCreateModalStatus: (status) =>
    set((state) => ({
      ...state,
      createModalStatus: status,
    })),

  fetchCustomerList: async (options) => {
    const prevData = options?.prevData || [];
    const page = options?.page || undefined;
    const searchValue = options?.searchValue || undefined;
    const queries = options?.queries || [];
    const sortStatus: NotNullableSortStatusT =
      options?.sortStatus || CUSTOMER_INITIAL_SORT_STATUS;
    const fiterSetQuery = options?.fiterSetQuery || INITIAL_FILTER_SET_QUERY;

    const order = sortStatus
      ? sortStatus.orderStatus === 'unset'
        ? 'asc'
        : sortStatus.orderStatus
      : 'asc';

    const { page: currPage } = get();

    const filterQueryArr = filterUtils.getFilterStatusQuery(
      fiterSetQuery || INITIAL_FILTER_SET_QUERY,
    );

    const paginationQueryArr = getPaginationQueries({
      queries: {},
      prevData: prevData || [],
      targetPage: page,
      currPage,
      sortby: sortStatus?.sortBy as keyof CustomerT,
      order,
      cursorKey: 'paginate_token',
      acc: !!page,
    });

    const queryArr = pushEntryIntoQueryArr(
      [...(queries || []), ...filterQueryArr, ...paginationQueryArr],
      {
        [CONTACT_COLUMNS.NAME_SEARCH]: searchValue || null,
      },
    );

    const query = buildQuery(queryArr);

    set({ listLoading: true });

    const result = await service.contact.fetchCustomerList(query);

    const customers = arrayToMap(result, 'alias');

    const newCursors = getCursorInfoV2(
      result,
      sortStatus?.sortBy as keyof CustomerT,
    );

    set({
      customerMap: customers,
      cursors: newCursors,
      page: page || 0,
      listLoading: false,
    });

    return result;
  },
  async fetchCustomer(alias) {
    const customer = await service.contact.fetchCustomer(alias);
    set((prev) => ({
      ...prev,
      customerMap: {
        ...prev.customerMap,
        [alias]: customer,
      },
    }));
    return customer;
  },
  changeCustomerAttr: (alias, target, value) => {
    if (target === 'company_info') {
      const company = value as CompanyInfoWithAlias;
      service.contact.updateCustomerAttr(alias, 'company', company.alias);
    } else {
      service.contact.updateCustomerAttr(alias, target, value);
    }
    get().setCustomerAttr(alias, target, value);
  },
  setCustomerAttr: (alias, target, value) => {
    const newMap = { ...get().customerMap };
    if (!newMap[alias]) return;
    newMap[alias] = {
      ...newMap[alias],
      [target]: value,
    };
    set({ customerMap: newMap });
  },
}));

export default useCustomerStore;
